/* eslint-disable max-lines */

import React, { useCallback, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Table from 'components/Table'
import Tooltip from 'components/Tooltip'

import ActionTakenInput from 'pages/shared/CustomerList/ActionTakenInput'
import AlertIconWithCount from 'pages/shared/Alert/IconWithCount'
import ArchiveDeviceModal from 'pages/shared/ArchivedDevices/ArchiveDeviceModal'
import ConnectedStatusIcon from 'pages/shared/ConnectedStatusIcon'
import ServiceContractIcon from 'pages/shared/DeviceTabs/ServiceContractIcon'

import Api from 'services/api'

import Brand from 'helpers/brand'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import useScreenSize from 'hooks/useScreenSize'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { ArchivedDevice } from 'types/ArchivedDevice'
import { Customer } from 'types/Customer'
import { Device } from 'types/Device'
import { longDateTime } from 'helpers/formatters'

type ArchiveDeviceState = {
  [index: string]: boolean
}

type Props = {
  customer: Customer
  updateRefreshTimestampCallback: () => void;
}

const CustomerDeviceList = ({ customer, updateRefreshTimestampCallback }: Props) => {
  const screenSize = useScreenSize()
  const [showArchiveDeviceModal, setShowArchiveDeviceModal] = useState<ArchiveDeviceState>({})
  const [hoveredDeviceId, setHoveredDeviceId] = useState('')

  const { enabledFeatures } = useFeaturesContext()

  const onCreateCallback = useCallback((device: Omit<ArchivedDevice, 'id' | 'createdAt'>) => {
    Api.ArchivedDevices.create(device.dealerId, device)
      .then(() => {
        updateRefreshTimestampCallback()
      })
  }, [updateRefreshTimestampCallback])

  const onDeleteSystem = useCallback((deviceId) => {
    if (confirm('Are you sure you want to delete this system? This cannot be undone.')) {
      Api.Device.delete(deviceId)
        .then(() => {
          updateRefreshTimestampCallback()
        })
    }
  }, [updateRefreshTimestampCallback])

  const getDeviceLink = (deviceId: string) => {
    if (enabledFeatures.includes('system_dashboard')) {
      return `${deviceId}/dashboard`
    } else {
      return `${deviceId}`
    }
  }

  return (
    <S.DevicesCell>
      {customer.devices?.map((device: Device) => (
        (device.matched &&
          <S.DeviceRow key={device.deviceId} onMouseEnter={() => { setHoveredDeviceId(device.deviceId) }} onMouseLeave={() => { setHoveredDeviceId('') }}>
            <S.DeviceCell>
              <S.DeviceTitle>
                <ServiceContractIcon displayIcon={hoveredDeviceId === device.deviceId} className={!device.serviceContractStatus ? 'serviceContractNone' : `serviceContract${device.serviceContractStatus}`} device={device} />
                <S.TitleArea>
                  <NavLink to={`/customers/${customer.id}/systems/${getDeviceLink(device.deviceId)}`}>
                    {device.optedIn ? (device.name || device.deviceId)
                      : (
                        <Tooltip title={device.status?.toLowerCase() === 'not enrolled' ? `Device not enrolled to customer's ${Brand.brandName().toUpperCase()} Home account.` : ''} placement="top">
                          <div>{device.status}</div>
                        </Tooltip>
                      )
                    }
                  </NavLink>
                  {device.optedIn && <S.ConnectedStatusIcon isConnected={device.connected} />}
                </S.TitleArea>
              </S.DeviceTitle>
              <S.Identifiers>
                <div><span>AUID: </span><S.Identifier>{device.deviceId}</S.Identifier></div>
                <div><span>S/N: </span><S.Identifier>{device.serialNumber}</S.Identifier></div>
              </S.Identifiers>
            </S.DeviceCell>

            <S.Cell>
              {device.optedIn &&
                <>
                  { screenSize === 'small' && <S.SmallScreenLabel>Model:</S.SmallScreenLabel> }
                  <span>{device.displayModel}</span>
                </>
              }
            </S.Cell>
            <S.AlertsCell>
              {device.optedIn && (
                <S.Alerts>
                  <AlertIconWithCount severity="critical" count={device.criticalAlertCount} />
                  <AlertIconWithCount severity="major" count={device.majorAlertCount} />
                  <AlertIconWithCount severity="minor" count={device.minorAlertCount} />
                  <AlertIconWithCount severity="major-sda" count={device.sdaMajorAlertCount} />
                  <AlertIconWithCount severity="minor-sda" count={device.sdaMinorAlertCount} />
                </S.Alerts>
              )}
            </S.AlertsCell>
            <S.Cell>
              {device?.createdAt &&
                <>
                  { screenSize === 'small' && <S.SmallScreenLabel>Date Added:</S.SmallScreenLabel> }
                  <span>{longDateTime(device?.createdAt)}</span>
                </>
              }
            </S.Cell>
            <S.ActionTakenCell>
              { screenSize === 'small' && <S.SmallScreenActionTakenLabel>Action Taken</S.SmallScreenActionTakenLabel> }
              <div>
                <ActionTakenInput device={device} />
              </div>

              {device.registered && device.optedIn
                ? (
                  <Tooltip title="Archive Device" placement="top">
                    <S.RemoveAction
                      onClick={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [device.deviceId]: true }))}
                    >
                      <S.ArchiveOutlinedIcon role="img" />
                    </S.RemoveAction>
                  </Tooltip>
                )
                : (
                  <Tooltip title="Delete Device" placement="top">
                    <S.RemoveAction
                      onClick={() => onDeleteSystem(device.deviceId)}
                    >
                      <S.DeleteOutlinedIcon role="img" />
                    </S.RemoveAction>
                  </Tooltip>
                )}

              <ArchiveDeviceModal
                isVisible={showArchiveDeviceModal[device.deviceId]}
                closeCallback={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [device.deviceId]: false }))}
                actionType="create"
                auid={device.deviceId}
                onUpdateCallback={async (reason) => onCreateCallback({ auid: device.deviceId, dealerId: device.dealerId, registered: device.registered, reason })}
              />
            </S.ActionTakenCell>
          </S.DeviceRow>
        )
      ))}
    </S.DevicesCell>)
}

export default CustomerDeviceList

const iconStyle = (iconType: string) => {
  return css`
    margin: 8px 0 0 10px;
    cursor: pointer;
    color: ${color(iconType)};

    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const Cell = styled(Table.Cell)`
  overflow: hidden;
  flex: 1;
  padding: 10px 0;

  > :first-child {
    margin-left: 15px;
  }

  @media ${screenSize.small} {
    padding: 5px 0 0 30px;

    > :first-child {
      margin-left: 0;
    }
  }
`

const SmallScreenLabel = styled.span`
  display: none;
  margin-right: 5px;

  @media ${screenSize.small} {
    display: block;
  }
`

const S = {
  Cell,
  DevicesCell: styled(Table.Cell)`
    overflow: hidden;
    flex: 5;
    flex-direction: column;
  `,
  DeviceRow: styled(Table.Row)`
    padding: 20px 0 20px 0;

    &:last-child {
      border-bottom: none;
    }

    & .serviceContract:focus .serviceContractNone{
      display: block;
    }
    &:hover .serviceContractNone,
    & .serviceContractExpired,
    & .serviceContractActive{
      display: block;
    }
  `,
  DeviceCell: styled(Table.Cell)`
    overflow: hidden;
    flex-direction: column;
  `,
  DeviceTitle: styled.div`
    display: grid;
    grid-template-columns: 30px 1fr 26px;
  `,
  TitleArea: styled.div`
    display: flex;
    flex-wrap: wrap;
  `,
  Identifiers: styled.div`
    padding-left: 30px;
    display: flex;
    flex-wrap: wrap;
  `,
  Identifier: styled.span`
    margin-right: 7px;
    overflow-wrap: anywhere;
  `,
  ConnectedStatusIcon: styled(ConnectedStatusIcon)`
    position: absolute;
    margin-top: 1px;
    margin-left: 10px;
  `,
  AlertsCell: styled(Table.Cell)`
    overflow: hidden;
    flex: 1;
    padding: 10px 0;

    @media ${screenSize.medium} {
      flex-wrap: wrap;
    }

    @media ${screenSize.small} {
      padding: 5px 0 0 25px;
      margin-bottom: -5px;
    }
  `,
  Alerts: styled.div`
    display: flex;
    flex-wrap: wrap;
    max-height: 70px; ${''/* limit the height so when alert icons wrap to the second row, there's not excessive space between rows */}
  `,
  RemoveAction: styled(KeyboardAccessible)`
    height: 40px;
  `,
  ActionTakenCell: styled(Cell)`
    flex-direction: row;
    margin-bottom: -15px;
    padding: 0;

    @media ${screenSize.small} {
      margin-left: 30px;
      margin-top: 5px;
    }
  `,
  DeleteOutlinedIcon: styled(DeleteOutlinedIcon)`
    ${iconStyle('destructiveIcon')}
    margin: 10px;
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
    ${iconStyle('actionableIcon')}
    margin: 10px;
  `,
  SmallScreenLabel,
  SmallScreenActionTakenLabel: styled(SmallScreenLabel)`
    text-wrap: nowrap;
    margin-top: 9px;
    margin-rigth: 10px;
  `
}
